import { styled } from "styled-components";

const Group = styled.div`
  display: flex;
  flex-direction: ${({ $vertical }) => ($vertical ? "column" : "row")};
  /* 세로 모드: 그룹 폭을 가장 넓은 칩에 맞추고(stretch) 모든 칩 너비를 통일 */
  align-items: ${({ $vertical }) => ($vertical ? "stretch" : "center")};
  width: ${({ $vertical }) => ($vertical ? "fit-content" : "auto")};
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  min-width: 70px;
  height: 35px;
  padding: 0 14px;
  border-radius: 5px;
  font-family: "Noto Sans KR";
  font-size: 15px;
  font-weight: 400;
  cursor: pointer;
  background-color: ${(props) => (props.$active ? "#0079e0" : "#ffffff")};
  color: ${(props) => (props.$active ? "#ffffff" : "#000000")};
  border: ${(props) =>
    props.$active ? "none" : "1px solid var(--admin-gray-stroke, #e8e8e8)"};
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

/**
 * 단일 선택 칩 그룹. 카테고리·구분/상태·빠른 기간 선택을 공통 UI로 통일한다.
 *
 * @param options [{ label, value, disabled? }] 또는 string[] (string이면 label=value)
 * @param value   현재 선택된 value
 * @param onChange (nextValue) => void
 * @param deselectTo 활성 칩을 다시 눌렀을 때 돌아갈 값.
 *   - '전체' 같은 기본 옵션이 있으면 그 값을 넘긴다(개별 칩만 토글, '전체' 칩은 토글 해제 안 됨).
 *   - 없으면 null(아무것도 선택 안 한 상태로 토글 해제).
 * @param allowDeselect 활성 칩 재클릭으로 해제(토글)를 허용할지. 기본 true.
 *   '전체'가 없고 항상 하나는 선택돼 있어야 하는 그룹은 false로 둔다.
 * @param vertical 칩을 한 줄에 하나씩 세로로 쌓는다. 기본 false(가로 배치).
 */
export function ChipGroup({
  options,
  value,
  onChange,
  deselectTo = null,
  allowDeselect = true,
  vertical = false,
  className,
}) {
  const normalized = options.map((option) =>
    typeof option === "object" ? option : { label: option, value: option },
  );

  const handleClick = (optionValue) => {
    const isActive = value === optionValue;
    if (!isActive) {
      onChange(optionValue);
      return;
    }
    if (!allowDeselect) {
      return; // 항상 하나는 선택을 유지해야 하는 그룹
    }
    // 활성 칩 재클릭: deselectTo로 복귀. 단, 자기 자신이 deselectTo면 유지.
    onChange(optionValue === deselectTo ? optionValue : deselectTo);
  };

  return (
    <Group className={className} $vertical={vertical}>
      {normalized.map((option) => (
        <Chip
          key={String(option.value)}
          type="button"
          $active={value === option.value}
          disabled={option.disabled}
          onClick={() => handleClick(option.value)}
        >
          {option.label}
        </Chip>
      ))}
    </Group>
  );
}

export default ChipGroup;
